import React from "react";
import { Box, Typography, Stack, Chip } from "@mui/material";
import { useSelector } from "react-redux";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import FadeInOnScroll from "../common/FadeInOnScroll";

const CITIES = ["Chennai", "Bangalore", "Hyderabad", "Mumbai", "PAN India"];

const HomePanIndia = () => {
  const theme = useSelector((s) => s.themeValue?.colors) || {};
  const start = theme.primary_gradient_start_color || "#FF2D95";
  const end = theme.primary_gradient_end_color || "#7B1FA2";
  const navy = theme.text || "#1A1A2E";

  return (
    <Box
      sx={{
        maxWidth: 1140,
        mx: "auto",
        px: { xs: 2, sm: 3, md: 4 },
        py: { xs: 1, md: 2 },
        mb: { xs: 3, md: 4 },
      }}
    >
      <Box
        sx={{
          position: "relative",
          borderRadius: { xs: "12px", sm: "16px" },
          overflow: "hidden",
          border: "1px solid rgba(123, 31, 162, 0.14)",
          boxShadow: "0 12px 48px rgba(26, 26, 46, 0.1)",
          background: `linear-gradient(145deg, ${start}18 0%, #ffffff 38%, ${end}12 100%)`,
        }}
      >
        <Box
          aria-hidden
          sx={{
            height: 4,
            background: `linear-gradient(90deg, ${start} 0%, ${end} 100%)`,
          }}
        />

        <Box
          aria-hidden
          sx={{
            position: "absolute",
            top: -80,
            right: -80,
            width: 280,
            height: 280,
            borderRadius: "50%",
            background: `radial-gradient(circle, ${start}22, transparent 70%)`,
            pointerEvents: "none",
          }}
        />
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            bottom: -60,
            left: -60,
            width: 220,
            height: 220,
            borderRadius: "50%",
            background: `radial-gradient(circle, ${end}18, transparent 70%)`,
            pointerEvents: "none",
          }}
        />

        <Box
          sx={{
            position: "relative",
            zIndex: 1,
            px: { xs: 2.5, sm: 4, md: 5 },
            py: { xs: 4, sm: 5, md: 5.5 },
            textAlign: "center",
          }}
        >
          <FadeInOnScroll delay={0}>
            <Box
              sx={{
                width: { xs: 64, sm: 72 },
                height: { xs: 64, sm: 72 },
                mx: "auto",
                mb: 2,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: `linear-gradient(135deg, ${start}, ${end})`,
                boxShadow: `0 8px 28px ${end}40`,
              }}
            >
              <LocationOnOutlinedIcon sx={{ fontSize: { xs: 34, sm: 38 }, color: "#fff" }} />
            </Box>
          </FadeInOnScroll>

          <FadeInOnScroll delay={60}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="center"
              spacing={1.5}
              sx={{ mb: 1.25 }}
            >
              <Box
                aria-hidden
                sx={{
                  width: { xs: 24, sm: 40 },
                  height: 1,
                  background: `linear-gradient(90deg, transparent, ${end}55)`,
                }}
              />
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 700,
                  letterSpacing: 2,
                  textTransform: "uppercase",
                  color: end,
                }}
              >
                Nationwide reach
              </Typography>
              <Box
                aria-hidden
                sx={{
                  width: { xs: 24, sm: 40 },
                  height: 1,
                  background: `linear-gradient(90deg, ${end}55, transparent)`,
                }}
              />
            </Stack>
          </FadeInOnScroll>

          <FadeInOnScroll delay={120}>
            <Typography
              component="h2"
              sx={{
                fontWeight: 800,
                letterSpacing: { xs: 0.8, sm: 1.2 },
                textTransform: "uppercase",
                fontSize: { xs: "1.35rem", sm: "1.65rem", md: "1.9rem" },
                color: navy,
                lineHeight: 1.2,
                mb: 1.5,
              }}
            >
              PAN India Presence
            </Typography>
          </FadeInOnScroll>

          <FadeInOnScroll delay={180}>
            <Typography
              sx={{
                color: "#5c6370",
                fontSize: { xs: 15, sm: 16, md: 17 },
                lineHeight: 1.75,
                maxWidth: 560,
                mx: "auto",
              }}
            >
              Connecting customers, builders and developers across India.
            </Typography>
          </FadeInOnScroll>

          <FadeInOnScroll delay={240}>
            <Typography
              sx={{
                mt: 2,
                mb: 3,
                fontStyle: "italic",
                fontWeight: 600,
                fontSize: { xs: "1rem", sm: "1.1rem" },
                background: `linear-gradient(90deg, ${start}, ${end})`,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              A Better Tomorrow Together
            </Typography>
          </FadeInOnScroll>

          <FadeInOnScroll delay={300}>
            <Stack
              direction="row"
              flexWrap="wrap"
              justifyContent="center"
              useFlexGap
              spacing={1}
              sx={{ gap: 1 }}
            >
              {CITIES.map((city) => {
                const isHighlight = city === "PAN India";
                return (
                  <Chip
                    key={city}
                    label={city}
                    size="medium"
                    sx={{
                      fontWeight: 600,
                      fontSize: { xs: 12, sm: 13 },
                      letterSpacing: 0.3,
                      height: 36,
                      borderRadius: "999px",
                      ...(isHighlight
                        ? {
                            color: "#fff",
                            background: `linear-gradient(90deg, ${start}, ${end})`,
                            border: "none",
                            boxShadow: `0 4px 14px ${end}35`,
                          }
                        : {
                            color: navy,
                            bgcolor: "rgba(255,255,255,0.85)",
                            border: `1px solid ${end}22`,
                            backdropFilter: "blur(4px)",
                          }),
                    }}
                  />
                );
              })}
            </Stack>
          </FadeInOnScroll>
        </Box>
      </Box>
    </Box>
  );
};

export default HomePanIndia;
